import React, { useState, useEffect, useMemo } from 'react';
import AdvancedFilterPanel, { FilterButton } from '../../components/common/AdvancedFilter';
import FilterAwarenessBar from '../../components/common/AdvancedFilter/FilterAwarenessBar';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import RateManagementForm from './components/RateManagementForm';
import ViewRate from './components/ViewRate';
import Alert from '../../components/Alert/Alert';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import { fetchRates, deleteRate, updateRate, fetchServiceOptions, fetchUnitTypeOptions, fetchWorkflowOptions, createRate } from './api/rateManagementApi';
import api from '../../api';
import { usePersistedFilters } from '../../hooks/usePersistedFilters';
import 'react-quill/dist/quill.snow.css';

const DEFAULT_PAGE_SIZE = 10;

const getEmptyFilters = () => ({
  service: '',
  standard: '',
  productCategory: '',
  serviceItem: '',
});

const RateManagement = () => {
  const { loadPersistedState, persistState } = usePersistedFilters('rate_management');

  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [filterData, setFilterData] = useState(getEmptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(getEmptyFilters);

  // Server-side pagination
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalElements, setTotalElements] = useState(0);

  const [rates, setRates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedRate, setSelectedRate] = useState(null);
  const [formErrorMsg, setFormErrorMsg] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  const [serviceOptions, setServiceOptions] = useState([]);
  const [unitTypeOptions, setUnitTypeOptions] = useState([]);
  const [workflowOptions, setWorkflowOptions] = useState([]);
  // Full unfiltered dataset used only for building filter option lists
  const [allRatesRaw, setAllRatesRaw] = useState([]);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const toggleRowExpansion = (rowId) => {
    setExpandedRows(prev => ({ ...prev, [rowId]: !prev[rowId] }));
  };

  // ── Fetch rates from server ──────────────────────────────────────────────────
  const loadRates = async (filters = appliedFilters, search = appliedSearch, pageNum = page, pageSize = size) => {
    setIsLoading(true);
    try {
      const data = await fetchRates({
        page: pageNum,
        size: pageSize,
        search,
        service: filters.service,
        standard: filters.standard,
        productCategory: filters.productCategory,
        serviceItem: filters.serviceItem,
      });

      const isPagedResponse = data && typeof data.totalElements === 'number' && Array.isArray(data.content);
      const rawList = isPagedResponse ? data.content : (Array.isArray(data) ? data : []);

      const mapItem = item => ({
        ...item,
        standard: Array.isArray(item.standards) ? item.standards.map(s => s.name).join(', ') : '',
        serviceItem: Array.isArray(item.serviceItems) ? item.serviceItems.map(si => si.name).join(', ') : '',
        productCategory: Array.isArray(item.productCategories) ? item.productCategories.map(pc => pc.name).join(', ') : '',
        level: Array.isArray(item.productLevels) ? item.productLevels.map(pl => pl.name).join(', ') : '',
        workflow: Array.isArray(item.priceSettings) ? item.priceSettings.map(ps => ps.workflow).filter(Boolean).join(', ') : '',
        priceList: Array.isArray(item.priceSettings)
          ? item.priceSettings.map(ps => (ps.rateFrom != null && ps.rateTo != null ? `${ps.rateFrom} - ${ps.rateTo}` : '')).filter(Boolean).join(', ')
          : '',
      });

      if (isPagedResponse) {
        // Backend returns proper Page<T> — use as-is
        setRates(rawList.map(mapItem));
        setTotalElements(data.totalElements);
      } else {
        // Backend returns flat array — apply client-side filtering + pagination as fallback
        let filtered = rawList;
        const q = (search ?? '').trim().toLowerCase();
        if (q) {
          filtered = filtered.filter(item => {
            const names = [
              ...(item.standards ?? []).map(s => s.name ?? ''),
              ...(item.serviceItems ?? []).map(s => s.name ?? ''),
              ...(item.productCategories ?? []).map(s => s.name ?? ''),
            ].join(' ').toLowerCase();
            return names.includes(q);
          });
        }
        if (filters.service) {
          filtered = filtered.filter(item => {
            const svc = (item.services ?? item.serviceName ?? '').toLowerCase();
            return svc.includes(filters.service.toLowerCase());
          });
        }
        if (filters.standard) {
          filtered = filtered.filter(item =>
            (item.standards ?? []).some(s => (s.name ?? '').toLowerCase().includes(filters.standard.toLowerCase()))
          );
        }
        if (filters.productCategory) {
          filtered = filtered.filter(item =>
            (item.productCategories ?? []).some(pc => (pc.name ?? '').toLowerCase().includes(filters.productCategory.toLowerCase()))
          );
        }
        if (filters.serviceItem) {
          filtered = filtered.filter(item =>
            (item.serviceItems ?? []).some(si => (si.name ?? '').toLowerCase().includes(filters.serviceItem.toLowerCase()))
          );
        }
        const total = filtered.length;
        const start = pageNum * pageSize;
        const page = filtered.slice(start, start + pageSize);
        setRates(page.map(mapItem));
        setTotalElements(total);
      }
    } catch (error) {
      console.error('Error loading rates:', error);
      showAlert('danger', 'Failed to load rates.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Initial load (restore persisted state) ───────────────────────────────────
  useEffect(() => {
    const saved = loadPersistedState();
    if (saved) {
      const f = saved.filters ?? getEmptyFilters();
      const s = saved.search ?? '';
      const p = saved.page ?? 0;
      const sz = saved.size ?? DEFAULT_PAGE_SIZE;
      setAppliedFilters(f);
      setFilterData(f);
      setAppliedSearch(s);
      setSearchQuery(s);
      setPage(p);
      setSize(sz);
      loadRates(f, s, p, sz);
    } else {
      loadRates();
    }

    fetchServiceOptions().then(setServiceOptions).catch(() => {});
    fetchUnitTypeOptions().then(setUnitTypeOptions).catch(() => {});
    fetchWorkflowOptions().then(setWorkflowOptions).catch(() => {});

    // Fetch full dataset once for filter option lists (independent of pagination)
    api.get('/crm/rates').then(res => {
      const raw = Array.isArray(res.data) ? res.data : (res.data?.content ?? []);
      setAllRatesRaw(raw);
    }).catch(() => {});
  }, []);

  // ── Search ───────────────────────────────────────────────────────────────────
  const handleSearch = () => {
    const q = (searchQuery ?? '').trim();
    setAppliedSearch(q);
    setPage(0);
    persistState({ filters: appliedFilters, search: q, page: 0, size });
    loadRates(appliedFilters, q, 0, size);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  // ── Filter ───────────────────────────────────────────────────────────────────
  const handleFilterChange = (name, value) => {
    setFilterData(prev => ({ ...prev, [name]: value }));
  };

  const handleFilterApply = () => {
    setAppliedFilters(filterData);
    setPage(0);
    persistState({ filters: filterData, search: appliedSearch, page: 0, size });
    loadRates(filterData, appliedSearch, 0, size);
  };

  const handleFilterReset = () => {
    const empty = getEmptyFilters();
    setFilterData(empty);
    setAppliedFilters(empty);
    setSearchQuery('');
    setAppliedSearch('');
    setPage(0);
    persistState({ filters: empty, search: '', page: 0, size });
    loadRates(empty, '', 0, size);
  };

  const standardOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    allRatesRaw.forEach(r => (r.standards ?? []).forEach(s => {
      if (s.name && !seen.has(s.name)) { seen.add(s.name); opts.push({ value: s.name, label: s.name }); }
    }));
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [allRatesRaw]);

  const productCategoryOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    allRatesRaw.forEach(r => (r.productCategories ?? []).forEach(pc => {
      if (pc.name && !seen.has(pc.name)) { seen.add(pc.name); opts.push({ value: pc.name, label: pc.name }); }
    }));
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [allRatesRaw]);

  const serviceItemOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    allRatesRaw.forEach(r => (r.serviceItems ?? []).forEach(si => {
      if (si.name && !seen.has(si.name)) { seen.add(si.name); opts.push({ value: si.name, label: si.name }); }
    }));
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [allRatesRaw]);

  const filterFields = [
    { key: 'service', label: 'Service', type: 'select', options: serviceOptions.map(s => ({ value: s.name, label: s.name })) },
    { key: 'standard', label: 'Standard', type: 'select', options: standardOptions },
    { key: 'productCategory', label: 'Product Category', type: 'select', options: productCategoryOptions },
    { key: 'serviceItem', label: 'Service Item', type: 'select', options: serviceItemOptions },
  ];

  const activeFilterCount = Object.values(appliedFilters).filter(v => v && v !== '').length
    + (appliedSearch ? 1 : 0);

  // ── CRUD ─────────────────────────────────────────────────────────────────────
  const handleFormSubmit = async (formData) => {
    const { standardId, serviceItemId, productCategory, level, ...rest } = formData;
    const cleanedFormData = {
      ...rest,
      standardIds: Array.isArray(standardId) ? standardId : (standardId ? [standardId] : []),
      serviceItemIds: Array.isArray(serviceItemId) ? serviceItemId : (serviceItemId ? [serviceItemId] : []),
      productCategoryIds: Array.isArray(productCategory) ? productCategory : (productCategory ? [productCategory] : []),
      productLevelIds: Array.isArray(level) ? level : (level ? [level] : []),
      priceSettings: formData.priceSettings.map(ps => {
        if (!ps.id || isNaN(Number(ps.id))) { const { id, ...psRest } = ps; return psRest; }
        return ps;
      }),
    };
    try {
      if (selectedRate) {
        await updateRate(selectedRate.id, cleanedFormData);
        showAlert('success', 'Rate updated successfully!');
      } else {
        await createRate(cleanedFormData);
        showAlert('success', 'Rate created successfully!');
      }
      await loadRates();
      setIsModalOpen(false);
      setSelectedRate(null);
      setFormErrorMsg('');
    } catch (error) {
      console.error('Error saving rate:', error);
      setFormErrorMsg('Failed to save data. Please check your input.');
      showAlert('danger', 'Failed to save rate. Please check your input.');
    }
  };

  const handleEdit = (row) => {
    const mapWorkflowId = (workflowName) => {
      const found = workflowOptions.find(wf => wf.workflowName === workflowName);
      return found ? found.id : '';
    };
    setSelectedRate({
      ...row,
      standardId: row.standards ? row.standards.map(s => s.id) : [],
      serviceItemId: row.serviceItems ? row.serviceItems.map(si => si.id) : [],
      productCategory: row.productCategories ? row.productCategories.map(pc => pc.id) : [],
      level: row.productLevels ? row.productLevels.map(pl => pl.id) : [],
      priceSettings: row.priceSettings
        ? row.priceSettings.map(ps => ({ ...ps, workFlowId: ps.workFlowId || mapWorkflowId(ps.workflow || ps.workflowName) }))
        : [],
    });
    setIsModalOpen(true);
  };

  const handleDelete = (row) => {
    setItemToDelete(row);
    setShowDeleteModal(true);
  };

  const handleView = (row) => {
    setSelectedRate(row);
    setIsViewModalOpen(true);
  };

  // ── Badge-list cell renderer (reused across columns) ─────────────────────────
  const makeBadgeCell = (key, badgeClass, maxVisible = 3) => ({
    key,
    label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
    width: '300px',
    className: 'text-truncate',
    render: (value, row) => {
      if (!value) return '-';
      const items = value.split(', ').filter(Boolean);
      const rowId = `${key}-${row.id}`;
      const isExpanded = expandedRows[rowId];
      const display = isExpanded ? items : items.slice(0, maxVisible);
      const remaining = items.length - maxVisible;
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '300px', alignItems: 'center' }}>
          {display.map((item, i) => (
            <span key={i} className={`badge ${badgeClass} text-white me-1 mb-1`} title={item} style={{ fontSize: '0.75rem' }}>{item}</span>
          ))}
          {remaining > 0 && !isExpanded && (
            <button className="btn btn-sm btn-outline-secondary" type="button" style={{ fontSize: '0.7rem', padding: '2px 6px', height: '20px', lineHeight: '1' }}
              onClick={e => { e.preventDefault(); e.stopPropagation(); toggleRowExpansion(rowId); }}>+{remaining}</button>
          )}
          {remaining > 0 && isExpanded && (
            <button className="btn btn-sm btn-outline-secondary" type="button" style={{ fontSize: '0.7rem', padding: '2px 6px', height: '20px', lineHeight: '1' }}
              onClick={e => { e.preventDefault(); e.stopPropagation(); toggleRowExpansion(rowId); }}>Show Less</button>
          )}
        </div>
      );
    },
  });

  const columns = [
    {
      key: 'services',
      label: 'Services',
      width: '200px',
      className: 'text-truncate',
      render: (services) => (
        <span title={services} style={{ display: 'inline-block', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {services}
        </span>
      ),
    },
    makeBadgeCell('standard', 'bg-primary'),
    makeBadgeCell('serviceItem', 'bg-blue'),
    makeBadgeCell('productCategory', 'bg-azure'),
    makeBadgeCell('level', 'bg-info'),
    {
      key: 'priceList',
      label: 'Price List',
      width: '300px',
      className: 'text-truncate',
      render: (priceList, row) => {
        if (!priceList) return '-';
        const prices = priceList.split(', ').filter(Boolean);
        const rowId = `priceList-${row.id}`;
        const isExpanded = expandedRows[rowId];
        const display = isExpanded ? prices : prices.slice(0, 3);
        const remaining = prices.length - 3;
        const formatIDR = (str) => {
          const nums = str.match(/[\d,]+/g);
          if (!nums) return str;
          return nums.map(n => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(parseInt(n.replace(/,/g, '')))).join(' - ');
        };
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '300px', alignItems: 'center' }}>
            {display.map((p, i) => <span key={i} className="badge bg-success text-white me-1 mb-1" title={formatIDR(p)} style={{ fontSize: '0.75rem' }}>{formatIDR(p)}</span>)}
            {remaining > 0 && !isExpanded && (
              <button className="btn btn-sm btn-outline-secondary" type="button" style={{ fontSize: '0.7rem', padding: '2px 6px', height: '20px', lineHeight: '1' }}
                onClick={e => { e.preventDefault(); e.stopPropagation(); toggleRowExpansion(rowId); }}>+{remaining}</button>
            )}
            {remaining > 0 && isExpanded && (
              <button className="btn btn-sm btn-outline-secondary" type="button" style={{ fontSize: '0.7rem', padding: '2px 6px', height: '20px', lineHeight: '1' }}
                onClick={e => { e.preventDefault(); e.stopPropagation(); toggleRowExpansion(rowId); }}>Show Less</button>
            )}
          </div>
        );
      },
    },
    makeBadgeCell('workflow', 'bg-teal'),
  ];

  return (
    <div className="container-xl">
      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      <div className="row g-2 align-items-center">
        <div className="col">
          <h3 className="page-title m-0">Service Item Rate Management</h3>
        </div>
        <div className="col-auto ms-auto d-print-none">
          <div className="btn-list">
            <button className="btn btn-primary" onClick={() => setIsModalOpen(true)}>
              <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                <path d="M12 5l0 14"></path><path d="M5 12l14 0"></path>
              </svg>
              Create
            </button>
          </div>
        </div>
      </div>

      {formErrorMsg && <div className="alert alert-danger mt-2">{formErrorMsg}</div>}

      <RateManagementForm
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedRate(null); setFormErrorMsg(''); }}
        onSubmit={handleFormSubmit}
        initialData={selectedRate}
        isEdit={!!selectedRate}
        serviceOptions={serviceOptions}
        unitTypeOptions={unitTypeOptions}
        workflowOptions={workflowOptions}
        formErrorMsg={formErrorMsg}
        onCloneSuccess={() => { loadRates(); showAlert('success', 'Rates cloned successfully!'); }}
      />

      <ViewRate
        isOpen={isViewModalOpen}
        onClose={() => { setIsViewModalOpen(false); setSelectedRate(null); }}
        data={selectedRate}
        unitTypeOptions={unitTypeOptions}
        workflowOptions={workflowOptions}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setItemToDelete(null); }}
        onConfirm={async () => {
          if (!itemToDelete?.id) return;
          setIsLoading(true);
          try {
            await deleteRate(itemToDelete.id);
            showAlert('success', 'Rate deleted successfully!');
            await loadRates();
          } catch (error) {
            showAlert('danger', 'Failed to delete rate.');
          } finally {
            setIsLoading(false);
          }
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
        title="Delete Rate"
        message="Are you sure you want to delete this? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      <div className="page-body">
        <div className="container-xl">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                  <li className="nav-item-tab" role="presentation">
                    <a href="#tabs-list-data" className="nav-link active" data-bs-toggle="tab" aria-selected="true" role="tab">List Data</a>
                  </li>
                </ul>
              </div>
              <div className="card-body">
                <div className="tab-content">
                  <div className="tab-pane active show" id="tabs-list-data" role="tabpanel">
                    <GenericView
                      data={rates}
                      columns={columns}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onView={handleView}
                      isLoading={isLoading}
                      filterComponent={
                        <FilterButton offcanvasId="offcanvasRateManagement" activeCount={activeFilterCount} />
                      }
                      searchComponent={
                        <SearchInput
                          value={searchQuery}
                          onChange={setSearchQuery}
                          onKeyPress={handleSearchKeyPress}
                          placeholder="Search rates... (Press Enter)"
                          id="rate-management-search"
                          autocomplete="off"
                        />
                      }
                      actionButtons={
                        <ActionButtons
                          onExportExcel={() => showAlert('info', 'Export to Excel coming soon')}
                          onExportPDF={() => showAlert('info', 'Export to PDF coming soon')}
                        />
                      }
                      belowTopControlsContent={
                        <FilterAwarenessBar
                          title="Rates is showing a filtered view"
                          summary={`Filtered by ${activeFilterCount} ${activeFilterCount === 1 ? 'criterion' : 'criteria'}`}
                          detail={Object.entries(appliedFilters).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                          loading={isLoading}
                          activeCount={activeFilterCount}
                          filterTags={Object.entries(appliedFilters).filter(([, v]) => v).map(([k, v]) => ({ key: k, label: `${k}: ${v}` }))}
                          onRemoveFilter={(key) => {
                            const next = { ...appliedFilters, [key]: '' };
                            setAppliedFilters(next);
                            setFilterData(next);
                            setPage(0);
                            persistState({ filters: next, search: appliedSearch, page: 0, size });
                            loadRates(next, appliedSearch, 0, size);
                          }}
                          onResetFilters={handleFilterReset}
                        />
                      }
                      pagination={{
                        currentPage: page + 1,
                        totalRows: totalElements,
                        rowsPerPage: size,
                        onPageChange: (newPage) => {
                          const next = newPage - 1;
                          setPage(next);
                          persistState({ filters: appliedFilters, search: appliedSearch, page: next, size });
                          loadRates(appliedFilters, appliedSearch, next, size);
                        },
                        onRowsPerPageChange: (newSize) => {
                          setSize(newSize);
                          setPage(0);
                          persistState({ filters: appliedFilters, search: appliedSearch, page: 0, size: newSize });
                          loadRates(appliedFilters, appliedSearch, 0, newSize);
                        },
                      }}
                      emptyStateMessage="No rates found"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AdvancedFilterPanel
        offcanvasId="offcanvasRateManagement"
        title="Filter Rates"
        filterData={filterData}
        onChange={handleFilterChange}
        onApply={handleFilterApply}
        onReset={handleFilterReset}
        filterFields={filterFields}
        showPeriod={false}
      />
    </div>
  );
};

export default RateManagement;
